import { variables, Flex, FlexProps } from '@bitrise/bitkit';
import { useGetNearestPoint, SeriesPointXY, Viz, VizSVG, VizSVGAxisX } from '@bitrise/bitviz';
import { SessionEvent } from '@trace/types';
import React, { PointerEvent, useEffect, useState, FunctionComponent } from 'react';
import useTrackingEventTimed from '../../Tracking/useTrackingEventTimed';
import { formatterMs } from '../../../utils/metric/getFormattedValue';
import transition from '../../ViewVisualisations/transition';
import SessionVisualisationTracker from '../SessionVisualisationTracker';
import SessionEventsVisualisationSeries from './SessionEventsVisualisationSeries';
import SessionEventsVisualisationTooltip from './SessionEventsVisualisationTooltip';

export const BAR_HEIGHT = 32;
export const BAR_PADDING = 4;
export const PADDING_BOTTOM = 24;
export const PADDING_HORIZONTAL = 64;
export const POINT_HEIGHT = BAR_HEIGHT - BAR_PADDING * 2;
export const POINT_WIDTH = 3;

export interface EventSeriesPoint {
  id: string;
  name: string;
  x: number;
  y: number;
}

interface Props extends FlexProps {
  end: number;
  events: SessionEvent[];
  focusX: null | number;
  onFocusX: (x: null | number) => void;
  start: number;
}

const SessionEventsVisualisation: FunctionComponent<Props> = (props) => {
  const { end, events, focusX, onFocusX, start, ...rest } = props;
  const [series, setSeries] = useState<EventSeriesPoint[]>([]);
  const [internalFocusPoint, setInternalFocusPoint] = useState<null | EventSeriesPoint>(null);
  const getNearestPoint = useGetNearestPoint(series);

  const handlePointerEnter = useTrackingEventTimed('SessionExplorer.Events.Interaction', {});

  const handleSetFocus = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point) {
      const nearestPoint = getNearestPoint(point, { axis: 'x' });

      onFocusX(point.x);

      if (nearestPoint) {
        setInternalFocusPoint(nearestPoint);
      }
    } else {
      onFocusX(null);
      setInternalFocusPoint(null);
    }
  };

  useEffect(() => {
    setSeries(
      events.map((event) => ({
        id: event.id,
        name: event.name,
        x: event.timestamp,
        y: 0.5,
      })),
    );
  }, [events]);

  return (
    <Flex {...rest} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerEnter.cancel}>
      <Flex>
        <Viz
          height={BAR_HEIGHT + PADDING_BOTTOM}
          onPointerLeave={handleSetFocus}
          onPointerMove={handleSetFocus}
          paddingBottom={PADDING_BOTTOM}
          paddingLeft={PADDING_HORIZONTAL}
          paddingRight={PADDING_HORIZONTAL}
          transition={transition}
          xDomain={[start, end]}
          yDomain={[0, 1]}
        >
          <VizSVG>
            <VizSVGAxisX
              animate
              fill={variables.colorGray5}
              format={(x) => formatterMs(x - start)}
              stroke={variables.colorGray3}
              tickValues={[start, start + (end - start) / 2, end].filter(Boolean)}
              ticksOnly
            />

            <SessionEventsVisualisationSeries focusPoint={internalFocusPoint} series={series} />

            <SessionVisualisationTracker paddingBottom={8} paddingTop={8} x={focusX} />
          </VizSVG>

          <SessionEventsVisualisationTooltip
            point={internalFocusPoint === null ? null : internalFocusPoint}
          />
        </Viz>
      </Flex>
    </Flex>
  );
};

export default SessionEventsVisualisation;
